import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Receipt,
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Smartphone,
  Crown,
  Sparkles,
  ShoppingCart,
  RotateCcw,
  XCircle,
  CheckCircle,
  TrendingUp,
  Activity,
  Clock,
  CreditCard,
  Eye,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import { useCompanyAdminAuth } from '../../contexts/CompanyAdminAuthContext';
import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
  <div className="bg-card rounded-lg border border-border p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
      </div>
      <div className={cn('p-3 rounded-lg', color)}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

const EventTypeBadge = ({ eventType }) => {
  const config = {
    purchase: { icon: ShoppingCart, color: 'bg-green-100 text-green-700', label: 'Purchase' },
    initial_buy: { icon: ShoppingCart, color: 'bg-green-100 text-green-700', label: 'Initial Buy' },
    subscribe: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Subscribe' },
    renewal: { icon: RotateCcw, color: 'bg-blue-100 text-blue-700', label: 'Renewal' },
    did_renew: { icon: RotateCcw, color: 'bg-blue-100 text-blue-700', label: 'Renewed' },
    auto_renew: { icon: RotateCcw, color: 'bg-blue-100 text-blue-700', label: 'Auto Renew' },
    cancel: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Cancelled' },
    cancelled: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Cancelled' },
    expired: { icon: Clock, color: 'bg-muted text-foreground', label: 'Expired' },
    admin_activate: { icon: CheckCircle, color: 'bg-purple-100 text-purple-700', label: 'Admin Activate' },
    admin_deactivate: { icon: XCircle, color: 'bg-purple-100 text-purple-700', label: 'Admin Deactivate' },
    admin_extend: { icon: TrendingUp, color: 'bg-purple-100 text-purple-700', label: 'Admin Extend' },
    admin_cancel: { icon: XCircle, color: 'bg-purple-100 text-purple-700', label: 'Admin Cancel' },
  };

  const { icon: EventIcon, color, label } = config[eventType] || { 
    icon: Activity, 
    color: 'bg-muted text-foreground', 
    label: eventType?.replace(/_/g, ' ') || 'Unknown' 
  };

  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium capitalize', color)}>
      <EventIcon className="w-3 h-3" />
      {label}
    </span>
  );
};

const SubscriptionTypeBadge = ({ type }) => {
  if (type === 'premium') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
        <Crown className="w-3 h-3" />
        Premium
      </span>
    );
  }
  if (type === 'ai_plan') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <Sparkles className="w-3 h-3" />
        AI Plan
      </span>
    );
  }
  return <span className="text-muted-foreground text-xs">-</span>;
};

const PlatformBadge = ({ platform }) => {
  const config = {
    ios: { color: 'bg-gray-800 text-white', label: 'iOS' },
    android: { color: 'bg-green-600 text-white', label: 'Android' },
    admin: { color: 'bg-purple-600 text-white', label: 'Admin' },
    admin_panel: { color: 'bg-purple-600 text-white', label: 'Admin' },
  };

  const { color, label } = config[platform] || { color: 'bg-muted text-foreground', label: platform || 'Unknown' };

  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', color)}>
      <Smartphone className="w-3 h-3" />
      {label}
    </span>
  );
};

const PaymentHistoryPage = () => {
  const { user } = useCompanyAdminAuth();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  
  // Filters
  const [search, setSearch] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('all');
  const [eventType, setEventType] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await companyAdminCustomersAPI.getPaymentHistoryStats();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: search.trim() || undefined,
        subscription_type: subscriptionType !== 'all' ? subscriptionType : undefined,
        event_type: eventType !== 'all' ? eventType : undefined,
        platform: platform !== 'all' ? platform : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      const res = await companyAdminCustomersAPI.getPaymentHistory(params);
      setHistory(res.data.history || []);
      setPagination((prev) => ({
        ...prev,
        total: res.data.total,
        pages: res.data.pages,
      }));
    } catch (error) {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, subscriptionType, eventType, platform, startDate, endDate]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearch('');
    setSubscriptionType('all');
    setEventType('all');
    setPlatform('all');
    setStartDate('');
    setEndDate('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleExport = async () => {
    try {
      toast.info('Preparing export...');
      const params = {
        subscription_type: subscriptionType !== 'all' ? subscriptionType : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      const res = await companyAdminCustomersAPI.exportPaymentHistory(params);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment_history_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export payment history');
    }
  };

  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailModalOpen(true);
  };

  // Get unique event types from stats for filter
  const eventTypes = stats?.event_types || [];

  return (
    <CompanyAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Payment History</h1>
            <p className="text-muted-foreground mt-1">View all subscription transactions and events</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExport} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={() => { loadStats(); loadHistory(); }} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Transactions"
              value={stats.total_transactions}
              icon={Receipt}
              color="bg-slate-500"
            />
            <StatCard
              title="Last 30 Days"
              value={stats.recent_transactions}
              icon={Activity}
              color="bg-blue-500"
            />
            <StatCard
              title="Active Subscriptions"
              value={stats.active_subscriptions}
              icon={CheckCircle}
              color="bg-green-500"
            />
            <StatCard
              title="Platforms"
              value={stats.by_platform?.length || 0}
              icon={Smartphone}
              color="bg-purple-500"
              subtext={stats.by_platform?.map(p => `${p._id}: ${p.count}`).join(', ')}
            />
          </div>
        )}

        {/* Breakdown Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* By Subscription Type */}
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                By Subscription Type
              </h3>
              <div className="space-y-2">
                {stats.by_subscription_type?.map((item) => (
                  <div key={item._id} className="flex items-center justify-between">
                    <SubscriptionTypeBadge type={item._id} />
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
                {(!stats.by_subscription_type || stats.by_subscription_type.length === 0) && (
                  <p className="text-sm text-muted-foreground">No data available</p>
                )}
              </div>
            </div>

            {/* By Event Type */}
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                By Event Type
              </h3>
              <div className="space-y-2 max-h-[150px] overflow-y-auto">
                {stats.by_event_type?.slice(0, 8).map((item) => (
                  <div key={item._id} className="flex items-center justify-between">
                    <EventTypeBadge eventType={item._id} />
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))}
                {(!stats.by_event_type || stats.by_event_type.length === 0) && (
                  <p className="text-sm text-muted-foreground">No data available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-card rounded-lg border border-border p-4">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm mb-1.5 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Customer name, email, transaction ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-[150px]">
                <Label className="text-sm mb-1.5 block">Subscription Type</Label>
                <Select value={subscriptionType} onValueChange={setSubscriptionType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="ai_plan">AI Plan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[150px]">
                <Label className="text-sm mb-1.5 block">Event Type</Label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Events" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    {eventTypes.map((et) => (
                      <SelectItem key={et} value={et} className="capitalize">
                        {et?.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[130px]">
                <Label className="text-sm mb-1.5 block">Platform</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="ios">iOS</SelectItem>
                    <SelectItem value="android">Android</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-[180px]">
                <Label className="text-sm mb-1.5 block">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="w-[180px]">
                <Label className="text-sm mb-1.5 block">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <Button type="submit">
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
              <Button type="button" variant="outline" onClick={handleClearFilters}>
                Clear
              </Button>
            </div>
          </form>
        </div>

        {/* History Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Subscription</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Event</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Platform</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Transaction ID</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold">Source</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No payment history found</td></tr>
                ) : (
                  history.map((record, idx) => (
                    <tr key={record._id || idx} className="border-b border-border hover:bg-muted">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium">{formatDate(record.createdAt)}</p>
                          <p className="text-xs text-muted-foreground">{new Date(record.createdAt).toLocaleTimeString()}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{record.customer_name || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">{record.customer_email || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <SubscriptionTypeBadge type={record.subscription_type} />
                      </td>
                      <td className="px-4 py-3">
                        <EventTypeBadge eventType={record.event_type} />
                      </td>
                      <td className="px-4 py-3">
                        <PlatformBadge platform={record.platform} adminName={record.admin_name} />
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-[150px] block">
                          {record.transaction_id || '-'}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-muted-foreground capitalize">
                          {record.source?.replace(/_/g, ' ') || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetail(record)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">Page {pagination.page} of {pagination.pages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>
              {selectedRecord?.customer_name} - {formatDateTime(selectedRecord?.createdAt)}
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Subscription Type</p>
                  <SubscriptionTypeBadge type={selectedRecord.subscription_type} />
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Event Type</p>
                  <EventTypeBadge eventType={selectedRecord.event_type} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedRecord.customer_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedRecord.customer_email || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Platform</p>
                  <PlatformBadge platform={selectedRecord.platform} adminName={selectedRecord.admin_name} />
                </div>
                <div>
                  <p className="text-muted-foreground">Source</p>
                  <p className="font-medium capitalize">{selectedRecord.source?.replace(/_/g, ' ') || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Date & Time</p>
                  <p className="font-medium">{formatDateTime(selectedRecord.createdAt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Product ID</p>
                  <p className="font-medium">{selectedRecord.product_id || '-'}</p>
                </div>
              </div>

              {selectedRecord.transaction_id && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Transaction ID</p>
                  <code className="text-sm break-all">{selectedRecord.transaction_id}</code>
                </div>
              )}

              {selectedRecord.raw_notification_type && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Notification Details</p>
                  <p className="text-sm">{selectedRecord.raw_notification_type}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </CompanyAdminLayout>
  );
};

export default PaymentHistoryPage;
